import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { User } from '../../services/authService';
import { cashflowService, CashflowEntry } from '../../services/cashflowService';
import CashflowList from './CashflowList';
import CashflowSummary from './CashflowSummary';
import MonthlySpend from './MonthlySpend';
import IncomeSpend from './IncomeSpend';
import InvestmentSpend from './InvestmentSpend';
import CashflowForm from './CashflowForm';
import './Cashflow.css';

interface CashflowPageProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type TabType = 'daily' | 'monthly' | 'income' | 'investment';

const CashflowPage: React.FC<CashflowPageProps> = ({ user, onToggleSidebar }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabType>('daily');
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CashflowEntry | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleAddClick = () => {
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleEditClick = (entry: CashflowEntry) => {
    setEditingEntry(entry);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingEntry(null);
    setRefreshKey(prev => prev + 1);
  };

  const handleFormSave = () => {
    // Refresh the list when an entry is saved
    setRefreshKey(prev => prev + 1);
  };

  const handleDeleteClick = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this entry?')) {
      try {
        await cashflowService.deleteCashflowEntry(id);
        setRefreshKey(prev => prev + 1);
      } catch (error) {
        console.error('Error deleting entry:', error);
        alert('Failed to delete entry');
      }
    }
  };

  // Check for create param from quick command
  useEffect(() => {
    const createParam = searchParams.get('create');
    if (createParam === 'true') {
      setEditingEntry(null);
      setShowForm(true);
      // Remove the param from URL
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  return (
    <div className="cashflow-page">
      <div className="cashflow-header">
        <div className="cashflow-header-left">
          <button className="cashflow-menu-btn" onClick={onToggleSidebar}>
            ☰
          </button>
          <button className="cashflow-home-btn" onClick={() => window.location.href = '/'}>
            🏠
          </button>
          <h1 className="cashflow-title">Cashflow</h1>
        </div>
        <div className="cashflow-header-right">
          <button className="cashflow-icon-btn" title="Search">
            🔍
          </button>
          <button className="cashflow-icon-btn" title="Refresh" onClick={() => setRefreshKey(prev => prev + 1)}>
            🔄
          </button>
        </div>
      </div>

      <div className="cashflow-content">
        {activeTab === 'daily' && (
          <CashflowList
            key={refreshKey}
            user={user}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />
        )}
        {activeTab === 'monthly' && (
          <MonthlySpend
            key={refreshKey}
            user={user}
            onEdit={handleEditClick}
          />
        )}
        {activeTab === 'income' && (
          <IncomeSpend
            key={refreshKey}
            user={user}
            onEdit={handleEditClick}
          />
        )}
        {activeTab === 'investment' && (
          <InvestmentSpend
            key={refreshKey}
            user={user}
            onEdit={handleEditClick}
          />
        )}
      </div>

      <button className="cashflow-fab" onClick={handleAddClick} title="Add Entry">
        +
      </button>

      <div className="cashflow-bottom-nav">
        <button
          className={`cashflow-nav-item ${activeTab === 'daily' ? 'active' : ''}`}
          onClick={() => setActiveTab('daily')}
        >
          <span className="cashflow-nav-icon">📋</span>
          <span className="cashflow-nav-label">Daily</span>
        </button>
        <button
          className={`cashflow-nav-item ${activeTab === 'monthly' ? 'active' : ''}`}
          onClick={() => setActiveTab('monthly')}
        >
          <span className="cashflow-nav-icon">🛒</span>
          <span className="cashflow-nav-label">Monthly</span>
        </button>
        <button
          className={`cashflow-nav-item ${activeTab === 'income' ? 'active' : ''}`}
          onClick={() => setActiveTab('income')}
        >
          <span className="cashflow-nav-icon">💰</span>
          <span className="cashflow-nav-label">Income</span>
        </button>
        <button
          className={`cashflow-nav-item ${activeTab === 'investment' ? 'active' : ''}`}
          onClick={() => setActiveTab('investment')}
        >
          <span className="cashflow-nav-icon">🏛️</span>
          <span className="cashflow-nav-label">Investment</span>
        </button>
      </div>

      {showForm && (
        <CashflowForm
          user={user}
          entry={editingEntry}
          onClose={handleFormClose}
          onSave={handleFormSave}
        />
      )}
    </div>
  );
};

export default CashflowPage;

